import { isEmpty } from 'lodash'
import React, { Suspense } from 'react'
import { Redirect, Route } from 'react-router-dom'
import { clearStoredSession, getLoggedInUserInfo } from 'utils'
import { userRoleEnums } from 'enums/userRole'

// Paths that stay reachable whatever the permission payload says: signing in, signing
// back in, the public candidate forms and the error pages the app itself redirects to.
// Without this, a session whose permissions have expired bounces /auth/login to
// /auth/404 and /auth/404 to itself, and the only way out is clearing browser storage.
const ALWAYS_REACHABLE_PATHS = [
  '/auth/login',
  '/auth/404',
  '/auth/500',
  'reset-password',
  'temporary-password-reset',
  'candidate-form',
  'candidate-bank-details-form',
]

// The access token cookie is still here but the permissions cookie that says what it may
// open has expired out from under it. That is a half-dead session, not a refusal, so drop
// what is left of it and send the user back to sign in on a clean slate.
const StaleSessionRedirect = () => {
  // Cleared during render rather than from an effect: <Redirect> navigates out of a class
  // componentDidMount, which runs in the layout phase and can unmount this component
  // before a passive useEffect ever fires. The login page then still sees the dead token
  // and bounces straight back to the dashboard, which is the loop this exists to break.
  clearStoredSession()

  return <Redirect to="/auth/login" />
}

const ProtectedRoute = ({ routerAnimation, Component, roles, modules, permission, ...rest }) => {
  const loggedInUserInfo = getLoggedInUserInfo()
  const userRoles = loggedInUserInfo?.userRoles || {}
  const subscribedModules = loggedInUserInfo?.subscribedModules || []
  const userRole = loggedInUserInfo?.userRole
  const hasSubscriptionScope = Boolean(loggedInUserInfo?.hasSubscriptionScope)
  const allowSubscriptionModules = hasSubscriptionScope && userRole === userRoleEnums?.ADMIN
  const hasPermissionMap =
    !isEmpty(userRoles) || (allowSubscriptionModules && subscribedModules.length > 0)
  // Nothing at all describes this session's access - neither its own permission map nor
  // the tenant's subscribed modules. Tenants always carry hasSubscriptionScope, so the
  // "no permission data yet, just render" branch below can never catch this for them.
  const hasAnyPermissionData = !isEmpty(userRoles) || subscribedModules.length > 0
  const isAlwaysReachable = ALWAYS_REACHABLE_PATHS.some((allowedPath) =>
    String(rest.path || '').includes(allowedPath),
  )

  if (userRoles !== undefined) {
    userRoles['404'] = ['all']
  }

  const hasPermissionAccess = (modulePermissions = [], requiredPermission) =>
    modulePermissions.includes(requiredPermission) || modulePermissions.includes('all')

  const hasModuleAccess = (moduleKey) =>
    Object.prototype.hasOwnProperty.call(userRoles, moduleKey) ||
    (allowSubscriptionModules && subscribedModules.includes(moduleKey))

  let isAuthenticate = !hasSubscriptionScope && userRole === userRoleEnums?.ADMIN
  if (hasPermissionMap) {
    for (let i = 0; i < modules?.length; i += 1) {
      const module = modules[i]
      if (
        module === 'profile' ||
        module === 'logIn' ||
        module === 'candidate_form' ||
        (module === 'employee_self_service' &&
          (userRole === userRoleEnums?.EMPLOYEE || userRole === userRoleEnums?.USER))
      ) {
        isAuthenticate = true
        break
      }
      if (permission) {
        if (
          (Object.prototype.hasOwnProperty.call(userRoles, module) &&
            hasPermissionAccess(userRoles[module], permission)) ||
          (allowSubscriptionModules && subscribedModules.includes(module))
        ) {
          isAuthenticate = true
          break
        } else {
          isAuthenticate = false
          break
        }
      }
      if (hasModuleAccess(module)) {
        isAuthenticate = true
        break
      }
    }
  }
  if (isAlwaysReachable) {
    isAuthenticate = true
  }
  return (
    <Route
      {...rest}
      render={() => {
        if (!isAlwaysReachable && loggedInUserInfo && !hasAnyPermissionData) {
          return <StaleSessionRedirect />
        }
        if (!hasPermissionMap && !hasSubscriptionScope) {
          return (
            <div className={routerAnimation}>
              <Suspense fallback={null}>
                <Component />
              </Suspense>
            </div>
          )
        }
        if (!isAuthenticate) {
          return <Redirect to="/auth/404" />
        }

        return (
          <div className={routerAnimation}>
            <Suspense fallback={null}>
              <Component />
            </Suspense>
          </div>
        )
      }}
    />
  )
}
export default ProtectedRoute
